import { useCallback, useEffect, useState } from "react";
import type { LatestReading } from "@spf/contracts";
import type { StreamStatus } from "@spf/api-client";
import { api } from "./client.js";
import { publishTelemetry } from "./alerts.js";

export interface LiveTelemetry {
  status: StreamStatus;
  /** Null until the first event arrives, which is the loading state. */
  latest: LatestReading | null;
  /** Reconnects now instead of waiting out the backoff. */
  retry: () => void;
}

/**
 * The live reading, held open for as long as the view is mounted.
 *
 * `useAsync` is the loader for every other view and is deliberately not used
 * here: it models one request that finishes, and this is a connection that is
 * meant not to. The states it produces are the same four, decided from the
 * connection's status and whether a reading has ever arrived.
 */
export function useLiveTelemetry(): LiveTelemetry {
  const [status, setStatus] = useState<StreamStatus>("connecting");
  const [latest, setLatest] = useState<LatestReading | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    const handle = api.streamTelemetry({
      onReading: (reading) => {
        if (live) setLatest(reading);
      },
      onStatus: (next) => {
        if (live) setStatus(next);
      },
    });

    return () => {
      // Nothing that arrives after unmount may set state, and the connection
      // must not outlive the view: a tab left open on another screen holding a
      // stream would spend one of the server's slots for nothing.
      live = false;
      handle.close();
    };
  }, [attempt]);

  const retry = useCallback(() => {
    setStatus("connecting");
    setAttempt((n) => n + 1);
  }, []);

  // The header's notifications bell reads from here rather than opening a
  // connection of its own. The server caps a user at four, and a badge is not
  // worth one of them.
  useEffect(() => {
    publishTelemetry({ latest, status });
  }, [latest, status]);

  return { status, latest, retry };
}
